import { call, put } from 'redux-saga/effects';
import { apiDashboard, apiGeneral, notify } from 'services';
import { requestErrorHandler, expirationDaysText } from 'utils';
import { AuthActions, LoginRequest } from 'store/ducks/auth';
import { LanguageActions } from 'store/ducks/language';
import {
  clearSessionActivity,
  recordSessionActivity,
} from 'utils/sessionActivity';

export const authRehydrateAccessToken = ({ payload }: Record<string, any>) => {
  if (!payload?.auth?.data) return;
  const { accessToken } = payload.auth.data;
  if (accessToken) {
    apiGeneral.defaults.headers.Authorization = `Bearer ${accessToken}`;
  }
};

const getDefaultLanguage = (data: any): string => {
  if (!data) return 'pt';

  if (typeof data.language === 'string') {
    return data.language;
  }

  if (data.country?.length) {
    const country = data.country[0].name?.toUpperCase();
    const spanishSpeakingCountries = [
      'ARGENTINA',
      'CHILE',
      'COLOMBIA',
      'EQUADOR',
      'GUATEMALA',
      'PERU',
    ];

    if (spanishSpeakingCountries.includes(country)) {
      return 'es';
    } else if (country === 'BRASIL') {
      return 'pt';
    }
  }

  return 'pt';
};

function* handleLoginSuccess(responseData: any) {
  const { token: accessToken, ...data } = responseData;

  yield call(apiDashboard.post, `login-dashboard`, {
    domain: 'EVONIK',
    token: accessToken,
  });

  apiGeneral.defaults.headers.Authorization = `Bearer ${accessToken}`;

  if (data.integration) {
    throw new Error('Usuário integração: Acesso não autorizado');
  }

  yield call(recordSessionActivity, Date.now());
  yield put(AuthActions.loginSuccess({ ...data, accessToken }));

  if (!data.country?.length) {
    notify('warning', 'Nenhum país encontrado. Atualize seu cadastro!');
  }

  const defaultLanguage = getDefaultLanguage(data);
  yield put(LanguageActions.setLanguage(defaultLanguage));

  notify('success', `Bem-vindo ${data.name}`);

  const { days_left_password }: { days_left_password: null | number } = data;

  if (days_left_password === null) {
    return;
  } else if (days_left_password <= 0) {
    return notify('error', `Sua senha expirou!`);
  } else if (days_left_password <= 10) {
    return notify('warning', expirationDaysText(days_left_password));
  }
}

export function* authLoginRequest(action: any) {
  try {
    const { data: postData } = action as LoginRequest;
    const { data: responseData } = yield call(
      apiGeneral.post,
      'login',
      postData,
    );
    yield handleLoginSuccess(responseData);
  } catch (error) {
    const { errorMessage } = requestErrorHandler(error);
    notify('error', errorMessage);
    yield put(AuthActions.loginFailure(errorMessage));
  }
}

export function* authFirebaseLoginRequest(action: any) {
  try {
    const { data: responseData } = yield call(
      apiGeneral.post,
      'firebase-login',
      action.data,
    );
    yield handleLoginSuccess(responseData);
  } catch (error) {
    const { errorMessage } = requestErrorHandler(error);
    notify('error', errorMessage);
    yield put(AuthActions.loginFailure(errorMessage));
  }
}

export function* authLogoutRequest() {
  try {
    yield call(apiGeneral.post, 'logout');
  } catch (error) {
    yield;
  } finally {
    notify('success', 'Até mais');
    yield call(clearSessionActivity);
    yield put(AuthActions.logoutSuccess());
    apiGeneral.defaults.headers.Authorization = null;
  }
}
